import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { Scene } from "@babylonjs/core/scene";

import type { Terrain } from "./terrain";

export interface ChunkedGroundOptions {
  /** Arête d'un chunk, en unités monde. */
  readonly chunkSize: number;
  /** Quads par côté de chunk. Définit la finesse du maillage. */
  readonly subdivisions: number;
  /** Rayon de la grille affichée, en chunks autour du joueur. */
  readonly viewRadius: number;
  /** Teinte des fonds de vallée. */
  readonly lowColor: Color3;
  /** Teinte des crêtes. */
  readonly highColor: Color3;
}

export const DEFAULT_CHUNKED_GROUND_OPTIONS: ChunkedGroundOptions = {
  // De grands chunks peu nombreux plutôt que l'inverse : à couverture égale,
  // c'est le nombre d'appels de dessin qui décide, pas le nombre de triangles.
  chunkSize: 48,
  // 2 unités par quad : huit échantillons sur la plus fine octave du terrain par
  // défaut (16 unités de longueur d'onde). Le maillage se voit de près, sous le
  // cube — c'est la distance à laquelle un facettage grossier se remarquerait.
  subdivisions: 24,
  // 11 × 11 chunks : le bord garanti est à 240 unités du joueur, la distance
  // qui laisse au brouillard la place d'installer un horizon crédible. ~139 000
  // triangles au total, mais le tri par frustum n'en dessine qu'un quart — la
  // grille couvre tout le tour du joueur, la caméra n'en regarde qu'un secteur.
  viewRadius: 5,
  // Contraste franc entre creux et crêtes : la teinte est le principal indice de
  // relief à distance, une seule lumière directionnelle ne suffit pas à faire
  // lire des collines douces.
  lowColor: new Color3(0.09, 0.15, 0.24),
  highColor: new Color3(0.63, 0.66, 0.54),
};

/** Un morceau de sol et les tampons qui le décrivent. */
interface Chunk {
  readonly mesh: Mesh;
  /**
   * Tampons **propres au chunk**, et non partagés : Babylon conserve la
   * référence du tableau passé à `updateVerticesData`, un tampon commun ferait
   * donc lire à tous les chunks la géométrie du dernier rempli.
   */
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  readonly colors: Float32Array;
  cx: number;
  cz: number;
}

function chunkKey(cx: number, cz: number): string {
  return `${cx}:${cz}`;
}

/**
 * Sol infini, pavé de chunks recyclés autour du joueur.
 *
 * Un monde sans limite ne peut pas être un grand mesh : à 6 unités par seconde,
 * n'importe quelle taille finie finit par être atteinte, et la mémoire d'un
 * maillage qui couvrirait « assez grand » serait déjà hors de portée. On garde
 * donc une grille de taille fixe centrée sur le joueur, et on la fait glisser :
 * les chunks sortis par l'arrière reviennent devant, re-remplis pour leurs
 * nouvelles coordonnées. Le nombre de meshes et de sommets ne dépend jamais de
 * la distance parcourue.
 *
 * Le recyclage est ce qui rend la traversée fluide : allouer et détruire des
 * meshes à chaque frontière franchie mettrait le ramasse-miettes à contribution
 * en plein déplacement, exactement au moment où l'on tient au taux d'images.
 */
export class ChunkedGround {
  readonly options: ChunkedGroundOptions;
  /** Taille d'un quad, en unités monde. */
  readonly cellSize: number;

  private readonly scene: Scene;
  private readonly terrain: Terrain;
  private readonly material: StandardMaterial;

  /** Sommets par côté de chunk. */
  private readonly side: number;
  /** Côté de la grille de hauteurs, débord d'un sommet compris. */
  private readonly paddedSide: number;
  /** Topologie commune à tous les chunks : elle ne dépend pas de leur position. */
  private readonly indices: Uint32Array;
  /**
   * Hauteurs du chunk en cours de remplissage, avec un sommet de débord de
   * chaque côté — de quoi calculer les normales des sommets de bord sans
   * ressortir du tableau. Purement temporaire, donc partagé sans risque.
   */
  private readonly heights: Float64Array;

  private readonly active = new Map<string, Chunk>();
  private readonly pool: Chunk[] = [];

  private centerX = 0;
  private centerZ = 0;
  private centered = false;
  private created = 0;
  private disposed = false;

  constructor(scene: Scene, terrain: Terrain, options: Partial<ChunkedGroundOptions> = {}) {
    this.scene = scene;
    this.terrain = terrain;
    this.options = { ...DEFAULT_CHUNKED_GROUND_OPTIONS, ...options };

    this.cellSize = this.options.chunkSize / this.options.subdivisions;
    this.side = this.options.subdivisions + 1;
    this.paddedSide = this.side + 2;
    this.heights = new Float64Array(this.paddedSide * this.paddedSide);
    this.indices = this.buildIndices();
    this.material = this.createMaterial();
  }

  /** Chunks affichés à cet instant. */
  get activeCount(): number {
    return this.active.size;
  }

  /** Meshes vivants, pool compris : c'est ce nombre qui doit rester constant. */
  get meshCount(): number {
    return this.active.size + this.pool.length;
  }

  /** Mesh occupant ce chunk, s'il est affiché. */
  chunkAt(cx: number, cz: number): Mesh | undefined {
    return this.active.get(chunkKey(cx, cz))?.mesh;
  }

  /**
   * Recentre la grille sur `(x, z)`.
   *
   * Sort immédiatement tant que le joueur n'a pas changé de chunk : appelée à
   * chaque image, la méthode ne fait un vrai travail qu'une fois toutes les
   * quelques secondes de marche.
   */
  update(x: number, z: number): void {
    if (this.disposed) return;

    const { chunkSize, viewRadius } = this.options;
    const cx = Math.floor(x / chunkSize);
    const cz = Math.floor(z / chunkSize);
    if (this.centered && cx === this.centerX && cz === this.centerZ) return;

    this.centerX = cx;
    this.centerZ = cz;
    this.centered = true;

    // Ce qui est sorti du rayon retourne au pool. Supprimer pendant l'itération
    // d'une `Map` est défini par la spécification : les entrées restantes sont
    // parcourues normalement.
    for (const [key, chunk] of this.active) {
      if (Math.abs(chunk.cx - cx) > viewRadius || Math.abs(chunk.cz - cz) > viewRadius) {
        chunk.mesh.setEnabled(false);
        this.active.delete(key);
        this.pool.push(chunk);
      }
    }

    // Ce qui manque est rempli, en puisant dans le pool avant d'allouer.
    for (let dz = -viewRadius; dz <= viewRadius; dz++) {
      for (let dx = -viewRadius; dx <= viewRadius; dx++) {
        const chunkX = cx + dx;
        const chunkZ = cz + dz;
        const key = chunkKey(chunkX, chunkZ);
        if (this.active.has(key)) continue;

        const chunk = this.pool.pop() ?? this.createChunk();
        this.fill(chunk, chunkX, chunkZ);
        this.active.set(key, chunk);
      }
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const chunk of this.active.values()) chunk.mesh.dispose();
    for (const chunk of this.pool) chunk.mesh.dispose();
    this.active.clear();
    this.pool.length = 0;
    this.material.dispose();
  }

  /**
   * Triangulation d'une grille régulière, commune à tous les chunks.
   *
   * Ordre `a, b, d` puis `a, d, c` sur le quad `a(i,j) b(i+1,j) c(i,j+1)
   * d(i+1,j+1)` : c'est l'enroulement dont la normale pointe vers le haut dans
   * le repère main gauche de Babylon. L'inverser rendrait le sol invisible,
   * éliminé par le tri des faces arrière.
   */
  private buildIndices(): Uint32Array {
    const { subdivisions } = this.options;
    const indices = new Uint32Array(subdivisions * subdivisions * 6);

    let n = 0;
    for (let j = 0; j < subdivisions; j++) {
      for (let i = 0; i < subdivisions; i++) {
        const a = j * this.side + i;
        const b = a + 1;
        const c = a + this.side;
        const d = c + 1;

        indices[n++] = a;
        indices[n++] = b;
        indices[n++] = d;
        indices[n++] = a;
        indices[n++] = d;
        indices[n++] = c;
      }
    }

    return indices;
  }

  private createMaterial(): StandardMaterial {
    const material = new StandardMaterial("ground-material", this.scene);
    // Blanc : Babylon multiplie la couleur de sommet par le diffus, un diffus
    // sombre étoufferait la teinte portée par le maillage.
    material.diffuseColor = Color3.White();
    material.specularColor = Color3.Black();

    return material;
  }

  private createChunk(): Chunk {
    const vertexCount = this.side * this.side;
    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    const colors = new Float32Array(vertexCount * 4);

    const mesh = new Mesh(`chunk-${this.created++}`, this.scene);
    const data = new VertexData();
    data.positions = positions;
    data.normals = normals;
    data.colors = colors;
    data.indices = this.indices;
    // `updatable` : sans ce drapeau, les tampons GPU seraient statiques et
    // `updateVerticesData` n'aurait aucun effet au recyclage.
    data.applyToMesh(mesh, true);

    mesh.material = this.material;
    mesh.receiveShadows = true;
    // Le sol n'est jamais désigné à la souris : l'écarter du test de sélection
    // évite de parcourir ses ~1 150 triangles à chaque clic.
    mesh.isPickable = false;

    return { mesh, positions, normals, colors, cx: 0, cz: 0 };
  }

  /** Lecture de la grille de hauteurs. Les indices sont bornés par construction. */
  private sampledHeight(i: number, j: number): number {
    return this.heights[j * this.paddedSide + i] ?? 0;
  }

  /** Réécrit un chunk pour de nouvelles coordonnées. */
  private fill(chunk: Chunk, cx: number, cz: number): void {
    const { chunkSize, lowColor, highColor } = this.options;
    const { amplitude } = this.terrain.options;
    const step = this.cellSize;
    const originX = cx * chunkSize;
    const originZ = cz * chunkSize;

    // Hauteurs, débord compris. Les échantillonner d'un bloc évite les quatre
    // appels supplémentaires par sommet qu'exigerait une normale calculée
    // point par point — un remplissage coûte ainsi le quart des évaluations
    // de bruit.
    for (let j = 0; j < this.paddedSide; j++) {
      const worldZ = originZ + (j - 1) * step;
      for (let i = 0; i < this.paddedSide; i++) {
        const worldX = originX + (i - 1) * step;
        this.heights[j * this.paddedSide + i] = this.terrain.heightAt(worldX, worldZ);
      }
    }

    const { positions, normals, colors } = chunk;
    let p = 0;
    let c = 0;

    for (let j = 0; j < this.side; j++) {
      const localZ = j * step;
      const pj = j + 1;

      for (let i = 0; i < this.side; i++) {
        const pi = i + 1;
        const height = this.sampledHeight(pi, pj);

        // Coordonnées **locales** : le mesh est ensuite posé à l'origine de son
        // chunk. Garder de petites valeurs de sommet préserve la précision des
        // flottants 32 bits loin de l'origine du monde, et laisse le tri par
        // frustum travailler chunk par chunk.
        positions[p] = i * step;
        positions[p + 1] = height;
        positions[p + 2] = localZ;

        // Différences centrées sur la grille échantillonnée : la normale ne
        // dépend que des coordonnées monde, donc deux chunks voisins trouvent
        // exactement la même valeur le long de leur arête commune. Une normale
        // moyennée par `VertexData.ComputeNormals`, qui n'a connaissance que
        // des triangles du chunk courant, laisserait une couture d'éclairage
        // visible à chaque frontière.
        const dhdx = (this.sampledHeight(pi + 1, pj) - this.sampledHeight(pi - 1, pj)) / (2 * step);
        const dhdz = (this.sampledHeight(pi, pj + 1) - this.sampledHeight(pi, pj - 1)) / (2 * step);
        const length = Math.sqrt(dhdx * dhdx + 1 + dhdz * dhdz);
        normals[p] = -dhdx / length;
        normals[p + 1] = 1 / length;
        normals[p + 2] = -dhdz / length;

        p += 3;

        // Teinte par altitude. Sans elle, des collines douces éclairées par une
        // seule directionnelle sont quasi illisibles : rien ne distingue un
        // fond de vallée d'un plateau.
        const t = amplitude === 0 ? 0.5 : (height / amplitude + 1) * 0.5;
        colors[c] = lowColor.r + (highColor.r - lowColor.r) * t;
        colors[c + 1] = lowColor.g + (highColor.g - lowColor.g) * t;
        colors[c + 2] = lowColor.b + (highColor.b - lowColor.b) * t;
        colors[c + 3] = 1;
        c += 4;
      }
    }

    chunk.cx = cx;
    chunk.cz = cz;
    // `updateExtends` sur les positions : sans lui, le tri par frustum
    // s'appuierait sur la boîte englobante de l'occupant précédent du mesh et
    // ferait disparaître des chunks à l'écran.
    chunk.mesh.updateVerticesData(VertexBuffer.PositionKind, positions, true);
    chunk.mesh.updateVerticesData(VertexBuffer.NormalKind, normals);
    chunk.mesh.updateVerticesData(VertexBuffer.ColorKind, colors);
    chunk.mesh.position.set(originX, 0, originZ);
    chunk.mesh.setEnabled(true);
  }
}
